import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tabs,
  Typography,
  Upload,
  App,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PictureOutlined,
  FontSizeOutlined,
} from "@ant-design/icons";
import type {
  ReadingContent,
  ReadingContentDraft,
  ReadingActivityDraft,
  ReadingMode,
  ReadingType,
  ReadingLevel,
  LocalBlock,
  PageGroup,
} from "../../services/reading-content/reading-content.types";
import {
  READING_TYPE_OPTIONS,
  READING_LEVEL_OPTIONS,
} from "../../services/reading-content/reading-content.types";
import { getReadingContentByResourceId } from "../../services/reading-content/reading-content.service";
import { customUpload } from "../../utils";
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  MAX_UPLOAD_IMAGE_SIZE_BYTES,
} from "../../utils/imageOptimizer";
import ReadingBlockRichText from "./ReadingBlockRichText";
import EditorComponent from "../EditorComponent/EditorComponent";
import "./readingContentEditor.scss";

const { Title, Text } = Typography;
const { Option } = Select;

interface ReadingContentEditorProps {
  resourceId?: number;
  onDataChange: (data: ReadingContentDraft) => void;
  initialData?: ReadingContent | null;
}

const generateTempId = (): string => crypto.randomUUID();

const buildActivitiesFromContent = (
  content: ReadingContent,
): ReadingActivityDraft[] => {
  if (content.activities && content.activities.length > 0) {
    return [...content.activities]
      .sort((a, b) => a.order - b.order)
      .map((activity) => ({
        id: activity.id,
        tempId: generateTempId(),
        title: activity.title ?? undefined,
        content: activity.content,
        order: activity.order,
      }));
  }

  // Backward-compatible fallback: legacy single `activity` HTML string,
  // wrapped as one block titled "Actividad 1".
  const legacyActivity = content.metadata?.activity;
  if (legacyActivity) {
    return [
      {
        tempId: generateTempId(),
        title: "Actividad 1",
        content: legacyActivity,
        order: 0,
      },
    ];
  }

  return [];
};

const buildDraftFromContent = (content: ReadingContent): ReadingContentDraft => {
  const pageMap = new Map<number, LocalBlock[]>();

  for (const block of content.blocks ?? []) {
    const pageNum = block.page_number ?? 1;
    const localBlock: LocalBlock = {
      id: block.id,
      tempId: generateTempId(),
      // Passthrough: preserve the original type instead of coercing every
      // non-image block to "text" (was silently dropping special blocks).
      type: block.type,
      content: block.content ?? undefined,
      imageUrl: block.image_url ?? undefined,
      sectionTitle: block.section_title ?? undefined,
      imagePosition: block.image_position ?? undefined,
      order: block.order,
    };

    if (!pageMap.has(pageNum)) {
      pageMap.set(pageNum, []);
    }
    pageMap.get(pageNum)!.push(localBlock);
  }

  // Sort pages by page number
  const sortedPages = Array.from(pageMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([pageNumber, blocks]) => ({
      pageNumber,
      blocks: blocks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }));

  return {
    title: content.title,
    mode: content.mode,
    coverUrl: content.cover_url ?? undefined,
    author: content.metadata?.author ?? undefined,
    illustrator: content.metadata?.illustrator ?? undefined,
    genre: content.metadata?.genre ?? undefined,
    estimatedReadingTimeMinutes:
      content.metadata?.estimated_reading_time_minutes ?? undefined,
    readingType: content.metadata?.reading_type ?? undefined,
    level: content.metadata?.level ?? undefined,
    extendedCredits: content.extended_credits ?? undefined,
    activities: buildActivitiesFromContent(content),
    pages: sortedPages.length > 0 ? sortedPages : [],
  };
};

const ReadingContentEditor = ({
  resourceId,
  onDataChange,
  initialData,
}: ReadingContentEditorProps) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<ReadingContentDraft>({
    title: "",
    mode: "fixed",
    pages: [],
    activities: [],
  });
  const [uploadingImages, setUploadingImages] = useState<Set<string>>(
    new Set(),
  );

  // Load existing data
  useEffect(() => {
    if (initialData) {
      setDraft(buildDraftFromContent(initialData));
      return;
    }

    if (resourceId) {
      setLoading(true);
      getReadingContentByResourceId(resourceId)
        .then((content) => {
          if (content) {
            setDraft(buildDraftFromContent(content));
          }
        })
        .catch((err) => {
          console.error("Error loading reading content:", err);
        })
        .finally(() => setLoading(false));
    }
  }, [resourceId, initialData]);

  // Notify parent on every draft change
  useEffect(() => {
    onDataChange(draft);
  }, [draft, onDataChange]);

  const updateDraft = (partial: Partial<ReadingContentDraft>) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  // --- Page operations ---
  const addPage = () => {
    const maxPageNum = draft.pages.reduce(
      (max, p) => Math.max(max, p.pageNumber),
      0,
    );
    setDraft((prev) => ({
      ...prev,
      pages: [
        ...prev.pages,
        { pageNumber: maxPageNum + 1, blocks: [] },
      ],
    }));
  };

  const removePage = (pageNumber: number) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.filter((p) => p.pageNumber !== pageNumber),
    }));
  };

  const movePage = (pageIndex: number, direction: "up" | "down") => {
    setDraft((prev) => {
      const pages = [...prev.pages];
      const targetIndex = direction === "up" ? pageIndex - 1 : pageIndex + 1;
      if (targetIndex < 0 || targetIndex >= pages.length) return prev;

      // Swap page numbers
      const tempNum = pages[pageIndex].pageNumber;
      pages[pageIndex] = {
        ...pages[pageIndex],
        pageNumber: pages[targetIndex].pageNumber,
      };
      pages[targetIndex] = {
        ...pages[targetIndex],
        pageNumber: tempNum,
      };

      // Swap positions in array
      [pages[pageIndex], pages[targetIndex]] = [
        pages[targetIndex],
        pages[pageIndex],
      ];

      return { ...prev, pages };
    });
  };

  // --- Block operations within a page ---
  const addBlockToPage = (
    pageNumber: number,
    type: "text" | "image",
  ) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) =>
        page.pageNumber === pageNumber
          ? {
              ...page,
              blocks: [
                ...page.blocks,
                {
                  tempId: generateTempId(),
                  type,
                  content: type === "text" ? "" : undefined,
                  order: page.blocks.length + 1,
                },
              ],
            }
          : page,
      ),
    }));
  };

  const removeBlock = (pageNumber: number, tempId: string) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) =>
        page.pageNumber === pageNumber
          ? {
              ...page,
              blocks: page.blocks.filter((b) => b.tempId !== tempId),
            }
          : page,
      ),
    }));
  };

  const updateBlockContent = (
    pageNumber: number,
    tempId: string,
    content: string,
  ) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) =>
        page.pageNumber === pageNumber
          ? {
              ...page,
              blocks: page.blocks.map((b) =>
                b.tempId === tempId ? { ...b, content } : b,
              ),
            }
          : page,
      ),
    }));
  };

  const updateBlockImage = (
    pageNumber: number,
    tempId: string,
    imageUrl: string,
  ) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) =>
        page.pageNumber === pageNumber
          ? {
              ...page,
              blocks: page.blocks.map((b) =>
                b.tempId === tempId ? { ...b, imageUrl } : b,
              ),
            }
          : page,
      ),
    }));
  };

  const moveBlock = (
    pageNumber: number,
    blockIndex: number,
    direction: "up" | "down",
  ) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) => {
        if (page.pageNumber !== pageNumber) return page;
        const blocks = [...page.blocks];
        const targetIndex =
          direction === "up" ? blockIndex - 1 : blockIndex + 1;
        if (targetIndex < 0 || targetIndex >= blocks.length) return page;
        [blocks[blockIndex], blocks[targetIndex]] = [
          blocks[targetIndex],
          blocks[blockIndex],
        ];
        return { ...page, blocks };
      }),
    }));
  };

  // --- Reading activity operations ---
  const addActivity = () => {
    setDraft((prev) => ({
      ...prev,
      activities: [
        ...prev.activities,
        {
          tempId: generateTempId(),
          title: undefined,
          content: "",
          order: prev.activities.length,
        },
      ],
    }));
  };

  const removeActivity = (tempId: string) => {
    setDraft((prev) => ({
      ...prev,
      activities: prev.activities
        .filter((a) => a.tempId !== tempId)
        .map((a, idx) => ({ ...a, order: idx })),
    }));
  };

  const updateActivityTitle = (tempId: string, title: string) => {
    setDraft((prev) => ({
      ...prev,
      activities: prev.activities.map((a) =>
        a.tempId === tempId ? { ...a, title: title || undefined } : a,
      ),
    }));
  };

  const updateActivityContent = (tempId: string, content: string) => {
    setDraft((prev) => ({
      ...prev,
      activities: prev.activities.map((a) =>
        a.tempId === tempId ? { ...a, content } : a,
      ),
    }));
  };

  const moveActivity = (activityIndex: number, direction: "up" | "down") => {
    setDraft((prev) => {
      const activities = [...prev.activities];
      const targetIndex =
        direction === "up" ? activityIndex - 1 : activityIndex + 1;
      if (targetIndex < 0 || targetIndex >= activities.length) return prev;
      [activities[activityIndex], activities[targetIndex]] = [
        activities[targetIndex],
        activities[activityIndex],
      ];
      return {
        ...prev,
        activities: activities.map((a, idx) => ({ ...a, order: idx })),
      };
    });
  };

  // --- Block image upload ---
  const validateImageBeforeUpload = (file: File) => {
    if (file.size > MAX_UPLOAD_IMAGE_SIZE_BYTES) {
      message.error("La imagen supera el tamaño máximo de 25 MB");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleBlockImageUpload = async (
    pageNumber: number,
    tempId: string,
    options: any,
  ) => {
    setUploadingImages((prev) => new Set(prev).add(tempId));
    try {
      const result = await customUpload(options);
      if ("fileName" in result && result?.fileName) {
        updateBlockImage(pageNumber, tempId, result.url || result.fileName);
      }
    } catch (error) {
      console.error("Error uploading block image:", error);
    } finally {
      setUploadingImages((prev) => {
        const next = new Set(prev);
        next.delete(tempId);
        return next;
      });
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="reading-editor-loading">
        <Spin tip="Cargando contenido de lectura..." />
      </div>
    );
  }

  const dataTabContent = (
    <>
      <Text type="secondary" className="reading-editor-subtitle">
        Organiza el contenido usando las flechas para reordenar
      </Text>

      {/* Reading metadata fields */}
      <div className="reading-editor-fields">
        <div className="reading-field-row">
          <label className="reading-field-label">Modo de lectura</label>
          <Select
            value={draft.mode}
            onChange={(val: ReadingMode) => updateDraft({ mode: val })}
            style={{ width: "100%" }}
          >
            <Option value="fixed">Fijo (paginas definidas)</Option>
            <Option value="dynamic">Dinamico (scroll continuo)</Option>
          </Select>
        </div>

        <div className="reading-field-row-inline">
          <div className="reading-field-inline">
            <label className="reading-field-label">Autor</label>
            <Input
              value={draft.author ?? ""}
              onChange={(e) => updateDraft({ author: e.target.value })}
              placeholder="Nombre del autor"
            />
          </div>
          <div className="reading-field-inline">
            <label className="reading-field-label">Nivel</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Selecciona un nivel"
              allowClear
              value={draft.level ?? undefined}
              onChange={(val: ReadingLevel | undefined) =>
                updateDraft({ level: val })
              }
            >
              {READING_LEVEL_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="reading-field-inline">
            <label className="reading-field-label">Género</label>
            {/* Género — usado como filtro en Comprensión Lectora. Guarda el
                enum en reading_type (selección única). */}
            <Select
              style={{ width: "100%" }}
              placeholder="Selecciona un género"
              allowClear
              value={draft.readingType ?? undefined}
              onChange={(val: ReadingType | undefined) =>
                updateDraft({ readingType: val })
              }
            >
              {READING_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="reading-field-inline">
            <label className="reading-field-label">
              Tiempo estimado (min)
            </label>
            <Input
              type="number"
              min={1}
              value={draft.estimatedReadingTimeMinutes ?? ""}
              onChange={(e) =>
                updateDraft({
                  estimatedReadingTimeMinutes: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
              placeholder="12"
            />
          </div>
        </div>

        <div className="reading-field-row">
          <label className="reading-field-label">
            Créditos extendidos (opcional)
          </label>
          <Input.TextArea
            value={draft.extendedCredits ?? ""}
            onChange={(e) =>
              updateDraft({ extendedCredits: e.target.value })
            }
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder="Créditos extendidos Ilustraciones originales de... Publicado originalmente por... bajo licencia Creative Commons Attribution 4.0. Adaptación al español y ajuste de nivel: equipo Emmi."
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Solo para lecturas adaptadas de StoryWeaver / BookDash. Se muestra
            al final de la lectura; si se deja vacío no aparece nada en la
            plataforma.
          </Text>
        </div>
      </div>

      {/* Pages / blocks section */}
      <div className="reading-pages-section">
        {draft.pages.map((page, pageIndex) => (
          <Card
            key={page.pageNumber}
            className="reading-page-card"
            title={
              <div className="reading-page-card-header">
                <Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={pageIndex === 0}
                    onClick={() => movePage(pageIndex, "up")}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={pageIndex === draft.pages.length - 1}
                    onClick={() => movePage(pageIndex, "down")}
                  />
                  <span>Bloque / pagina {page.pageNumber}</span>
                </Space>
                <Space size="small">
                  <Popconfirm
                    title="Eliminar esta pagina y todo su contenido?"
                    onConfirm={() => removePage(page.pageNumber)}
                    okText="Si"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </Space>
              </div>
            }
          >
            {/* Blocks within the page */}
            {page.blocks.map((block, blockIndex) => (
              <div key={block.tempId} className="reading-block-item">
                <div className="reading-block-item-header">
                  <Space>
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={blockIndex === 0}
                      onClick={() =>
                        moveBlock(page.pageNumber, blockIndex, "up")
                      }
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={blockIndex === page.blocks.length - 1}
                      onClick={() =>
                        moveBlock(page.pageNumber, blockIndex, "down")
                      }
                    />
                    <Text strong>
                      {block.type === "text" ? "Texto" : "Imagen"}
                    </Text>
                  </Space>
                  <Space size="small">
                    {block.type === "image" && block.imageUrl && (
                      <Upload
                        customRequest={(options) =>
                          handleBlockImageUpload(
                            page.pageNumber,
                            block.tempId,
                            options,
                          )
                        }
                        showUploadList={false}
                        accept={ACCEPTED_IMAGE_MIME_TYPES}
                        beforeUpload={validateImageBeforeUpload}
                        disabled={uploadingImages.has(block.tempId)}
                      >
                        <Button type="link" size="small">
                          Reemplazar imagen
                        </Button>
                      </Upload>
                    )}
                    <Popconfirm
                      title={`Eliminar ${block.type === "text" ? "texto" : "imagen"}?`}
                      onConfirm={() =>
                        removeBlock(page.pageNumber, block.tempId)
                      }
                      okText="Si"
                      cancelText="No"
                    >
                      <Button type="link" size="small" danger>
                        Eliminar {block.type === "text" ? "texto" : "imagen"}
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>

                {block.type === "text" && (
                  <ReadingBlockRichText
                    value={block.content ?? ""}
                    onChange={(html) =>
                      updateBlockContent(page.pageNumber, block.tempId, html)
                    }
                    placeholder="Escribe el contenido de texto aqui..."
                  />
                )}

                {block.type === "image" && (
                  <div className="reading-block-image-container">
                    {uploadingImages.has(block.tempId) && (
                      <Spin tip="Subiendo imagen..." />
                    )}
                    {block.imageUrl && (
                      <div className="reading-block-image-preview">
                        <img
                          src={block.imageUrl}
                          alt="Preview del bloque"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 300,
                            objectFit: "contain",
                            borderRadius: 6,
                            border: "1px solid var(--bo-card-border)",
                          }}
                        />
                      </div>
                    )}
                    {!block.imageUrl && !uploadingImages.has(block.tempId) && (
                      <Upload
                        customRequest={(options) =>
                          handleBlockImageUpload(
                            page.pageNumber,
                            block.tempId,
                            options,
                          )
                        }
                        showUploadList={false}
                        accept={ACCEPTED_IMAGE_MIME_TYPES}
                        beforeUpload={validateImageBeforeUpload}
                      >
                        <Button icon={<UploadOutlined />}>
                          Subir imagen
                        </Button>
                      </Upload>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Add block buttons */}
            <div className="reading-page-add-buttons">
              <Button
                type="dashed"
                icon={<PictureOutlined />}
                onClick={() => addBlockToPage(page.pageNumber, "image")}
              >
                Agregar imagen
              </Button>
              <Button
                type="dashed"
                icon={<FontSizeOutlined />}
                onClick={() => addBlockToPage(page.pageNumber, "text")}
              >
                Agregar texto
              </Button>
            </div>
          </Card>
        ))}

        {/* Add page button */}
        <Button
          className="reading-add-page-button"
          icon={<PlusOutlined />}
          onClick={addPage}
          block
        >
          + Agregar bloque / pagina
        </Button>
      </div>
    </>
  );

  const activityTabContent = (
    <div className="reading-activities-section">
      {draft.activities.length === 0 ? (
        <div className="reading-activities-empty">
          <Text type="secondary">
            Esta lectura no tiene actividades todavía.
          </Text>
        </div>
      ) : (
        draft.activities.map((activity, activityIndex) => (
          <Card
            key={activity.tempId}
            className="reading-activity-card"
            title={
              <div className="reading-activity-card-header">
                <Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={activityIndex === 0}
                    onClick={() => moveActivity(activityIndex, "up")}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={activityIndex === draft.activities.length - 1}
                    onClick={() => moveActivity(activityIndex, "down")}
                  />
                  <span>
                    {activity.title?.trim()
                      ? activity.title
                      : `Actividad ${activityIndex + 1}`}
                  </span>
                </Space>
                <Popconfirm
                  title="¿Eliminar esta actividad?"
                  onConfirm={() => removeActivity(activity.tempId)}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </div>
            }
          >
            <div className="reading-field-row">
              <label className="reading-field-label">Título (opcional)</label>
              <Input
                value={activity.title ?? ""}
                placeholder={`Actividad ${activityIndex + 1}`}
                onChange={(e) =>
                  updateActivityTitle(activity.tempId, e.target.value)
                }
              />
            </div>

            <div className="reading-field-row">
              <label className="reading-field-label">Contenido</label>
              <EditorComponent
                text={activity.content}
                onContentChange={(html) =>
                  updateActivityContent(activity.tempId, html)
                }
              />
            </div>
          </Card>
        ))
      )}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addActivity}
        block
        className="reading-add-activity-button"
      >
        + Agregar actividad
      </Button>
    </div>
  );

  return (
    <div className="reading-content-editor">
      <div className="reading-editor-section-header">
        <Title level={4} style={{ margin: 0 }}>
          Contenido de lectura
        </Title>
      </div>

      <Tabs
        defaultActiveKey="data"
        items={[
          {
            key: "data",
            label: "Datos",
            children: dataTabContent,
          },
          {
            key: "activity",
            label: "Actividad de lectura",
            children: activityTabContent,
          },
        ]}
      />
    </div>
  );
};

export default ReadingContentEditor;
